/**
axios封装
 * 请求拦截、相应拦截、错误统一处理
 */
import axios from 'axios';
import router from '../router';
import config from '../config';
import cookie from "../service/cookie";
import {message} from "../commonJs/ResetMessage";
import httpClient from './requestInterceptor';
import crypto from '../service/crypto';
import pub from '../service/public';

/**
 * 提示函数
 * 显示2秒后关闭
 */
function tip(msg, type = 'error') {
  message({
    message: msg,
    type: type,
    duration: 2000,
    showClose: true
  });
}

let api_version = 'v2.8.0';

// 请求超时时间
axios.defaults.timeout = 10000;

// post请求头改为 JSON
axios.defaults.headers.post['Content-Type'] = 'application/json';

// 响应拦截器
axios.interceptors.response.use(
  response => {
    if (response.status === 200) {
      return Promise.resolve(response);
    } else {
      return Promise.reject(response);
    }
  },
  // 服务器状态码不是200的情况
  error => {
    if(error.code == 'ECONNABORTED' && error.message.indexOf('timeout') !== -1){
      tip('请求超时，请重新刷新页面');
      return;
    }
    if (error.response && error.response.status) {
      switch (error.response.status) {
        // 401: 未登录
        // 未登录则跳转登录页面，并携带当前页面的路径
        // 在登录成功后返回当前页面，这一步需要在登录页操作。
        case 401:
          router.replace({
            path: '/login',
            query: {redirect: router.currentRoute.fullPath}
          });
          break;
        // 403 token过期
        // 登录过期对用户进行提示
        // 清除本地token和清空vuex中token对象
        // 跳转登录页面
        case 403:
          tip('登录过期，请重新登录');
          // 清除token
          cookie.setCookie('28project_usertoken', '');
          // 立即跳转登录页面，并将要浏览的页面fullPath传过去，登录成功后跳转需要访问的页面
          router.replace({
            path: '/login',
            query: {
              redirect: router.currentRoute.fullPath
            }
          });
          break;
        // 404请求不存在
        case 404: {
          const normalized = {
            ...error.response,
            data: {
              code: 404,
              message: '接口未定义'
            }
          };
          tip(normalized.data.message);
          // 404 直接当作业务层可处理的结果返回
          return Promise.resolve(normalized);
        }
        // 500 服务器错误
        case 500: {
          const normalized = {
            ...error.response,
            data: {
              code: 500,
              message: '服务器错误'
            }
          };
          tip(normalized.data.message);
          return Promise.resolve(normalized);
        }
        default:
          tip(error.response.data.message);
      }
      return Promise.reject(error.response);
    }
  }
);

/**
 * get方法，对应get请求
 * @param {String} url [请求的url地址]
 * @param {Object} Req [请求时携带的参数]
 * @param {String} apiType [客户端类型，'pc' | 'mobile' | 'wx'，默认 'pc']
 */
async function fetchGet(url, Req, apiType='pc') {
  let params = {
    ver: api_version, // v1
    agent: apiType, // mobile | pc | wx
    req: Req
  };

  // 根据 API 类型选择不同根路径（与 fetchUsrPost 保持一致）
  let base_url = config.getApiRootSync();

  // 加密请求参数（内部会获取服务器时间戳）
  const encryptedParams = await crypto.encryptRequest(params);

  return new Promise((resolve, reject) => {
        axios.get(base_url + url, {
      headers: {
        'Content-Type': "application/json",
        'Authorization': pub.getToken()
      },
      params: encryptedParams
    }).then(res => {
      resolve(res.data);
    }).catch(err => {
      // 直接返回原始错误对象，便于上层统一处理
      reject(err);
    })
  });
}

/**
 * 使用 httpClient 的 post 方法（带 token 自动刷新）
 * @param {String} url [请求的url地址]
 * @param {Object} Req [请求时携带的参数]
 * @param {String} apiType [客户端类型，'pc' | 'mobile' | 'wx'，默认 'pc']
 */
async function fetchPost(url, Req, apiType='pc') {
  // 统一构造请求体为 JSON
  let params = {
    ver: api_version,
    agent: apiType,
    req: Req
  };

  // 根据 API 类型选择不同根路径
  let api_url = config.getApiRootSync();

  const fullUrl = api_url + url;

  // 导出类接口适当放宽超时时间
  if(url.indexOf('Export') !== -1){
    axios.defaults.timeout = 60000;
  } else {
    axios.defaults.timeout = 10000;
  }

  if(!pub.isLoggedIn() && !url.includes('UsrLogin')){
    tip('登录过期，请重新登录');
    pub.goLogin(router.currentRoute.fullPath);
    return;
  }

  // 加密请求参数（内部会获取服务器时间戳）
    const encryptedParams = await crypto.encryptRequest(params);

  return new Promise((resolve, reject) => {
    httpClient.post(fullUrl, encryptedParams, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(res => {
        resolve(res.data);
      })
      .catch(err => {
        reject(err);
      });
  });
}

/**
 * 文件下载方法（返回blob）
 * @param {String} url [请求的url地址]
 * @param {Object} Req [请求时携带的参数]
 * @param {String} apiType [客户端类型，'pc' | 'mobile' | 'wx'，默认 'pc']
 */
async function fetchDownload(url, Req, apiType='pc') {
  let params = {
    ver: api_version,
    agent: apiType,
    req: Req
  };
  
  let api_url = config.getApiRootSync();

  const fullUrl = api_url + url;

  if(!pub.isLoggedIn() && !url.includes('UsrLogin')){
    tip('登录过期，请重新登录');
    pub.goLogin(router.currentRoute.fullPath);
    return;
  }

  // 加密请求参数（内部会获取服务器时间戳）
    const encryptedParams = await crypto.encryptRequest(params);

  return new Promise((resolve, reject) => {
    httpClient.post(fullUrl, encryptedParams, {
      headers: {
        'Content-Type': 'application/json'
      },
      responseType: 'blob'
    })
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });
}

export default {
  fetchGet: fetchGet,
  fetchPost: fetchPost,
  fetchDownload: fetchDownload
}



// WEBPACK FOOTER //
// ./src/request/request.js